import { useCallback, useEffect, useState } from "react";
import type { ContactDetail } from "@multi-chat/shared";
import { getContact } from "../../api/contacts-api.js";

export type SavedContactDetailState =
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "ready"; contact: ContactDetail }
  | { kind: "error" };

type SettledState = Extract<SavedContactDetailState, { kind: "ready" | "error" }>;

/**
 * Read-only saved-contact detail (groups and channel profiles). Pass `contactId: null` for
 * unsaved or deleted senders; nothing is fetched.
 */
export function useSavedContactDetail(
  organizationId: number,
  contactId: number | null,
): { state: SavedContactDetailState; retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<{ key: string; state: SettledState } | null>(null);
  const key = contactId == null ? null : `${organizationId}:${contactId}:${attempt}`;

  useEffect(() => {
    if (key == null || contactId == null) return;
    const controller = new AbortController();
    getContact(organizationId, contactId, controller.signal).then(
      (contact) => {
        if (!controller.signal.aborted) setSettled({ key, state: { kind: "ready", contact } });
      },
      () => {
        if (!controller.signal.aborted) setSettled({ key, state: { kind: "error" } });
      },
    );
    return () => controller.abort();
  }, [key, organizationId, contactId]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const state: SavedContactDetailState =
    key == null ? { kind: "none" } : settled?.key === key ? settled.state : { kind: "loading" };
  return { state, retry };
}
